import { useCallback } from 'react';
import { uniqBy } from 'lodash';

import {
  ActionsData,
  FileTreeItemType,
  Ryecon,
  RyeconColorSnowflake,
  RyeconDatabase,
  RyeconDatabaseConnection,
  RyeconDatabaseSchema,
  RyeconEllipsisHorizontal,
  RyeconTable,
  Sizes,
} from '@dbt-labs/sourdough';

import { DataPlatform, WarehouseEntityType } from '@/__generated__/graphql';
import {
  useAddFavoriteAsset,
  useDeleteFavoriteAsset,
  useFavoriteAssets,
} from '@/hooks/admin/favoriteAssets';
import {
  useAddFavoriteProject,
  useDeleteFavoriteProject,
  useFavoriteProjectIds,
} from '@/hooks/admin/favoriteProjects';
import { Project } from '@/hooks/admin/project';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { useDbtCloudAccount } from '../DbtCloudProvider';
import { RyeconFavoriteFilled, RyeconFavoriteOutline } from './RyeconFavorite';

/** Additional data for file tree items */
export type InfoDataType = {
  text?: string;
  icon?: {
    ryecon: Ryecon;
    label: string;
  };
  environmentIds?: number[];
};

type TreeNodeData = {
  name?: string;
  iconOverride?: { ryecon: Ryecon; label: string; className?: string };
  data?: {
    disabled?: boolean;
    info?: InfoDataType;
    pagination?: {
      page: number;
      hasMore: boolean;
      total: number;
      isLoading: boolean;
    };
    tooltipText?: string;
    rawData?: Record<string, unknown>;
  } & ActionsData;
};

export const generateFolder = (
  relativePath: string,
  additionalData?: TreeNodeData,
): FileTreeItemType => {
  const parts = relativePath.split('/');
  const inferredName = parts.pop() || '';
  const parent = parts.join('/') || 'root';
  return {
    id: relativePath,
    parent,
    text: additionalData?.name || inferredName,
    data: {
      pathType: 'directory',
      name: additionalData?.name || inferredName,
      relativePath,
      depth: parts.length,
      ...additionalData?.data,
    },
  };
};

export const generateFolderWithIcon = (
  relativePath: string,
  additionalData?: TreeNodeData,
): FileTreeItemType => {
  const folder = generateFolder(relativePath, additionalData);
  if (additionalData?.iconOverride) {
    folder.data.iconOverride = additionalData.iconOverride;
  }
  return folder;
};

export const generateFile = (
  relativePath: string,
  additionalData?: TreeNodeData,
): FileTreeItemType => {
  const parts = relativePath.split('/');
  const inferredName = parts.pop() || '';
  const parent = parts.join('/') || 'root';
  return {
    id: relativePath,
    parent,
    text: additionalData?.name || inferredName,
    data: {
      pathType: 'file',
      name: additionalData?.name || inferredName,
      relativePath,
      depth: parts.length,
      ...additionalData?.data,
    },
  };
};

export const generateFileWithLogo = (
  relativePath: string,
  additionalData?: TreeNodeData,
): FileTreeItemType => {
  const file = generateFile(relativePath, additionalData);
  if (additionalData?.iconOverride) {
    file.data.iconOverride = additionalData.iconOverride;
  }
  return file;
};

type ProjectInfo = {
  projectId: number;
  resourceType?: NavigationResourceType;
};

// project items are in the format of `<root>/<project-name>:<project-id>`
export const parseProject = (item: string | undefined): ProjectInfo | null => {
  if (!item) return null;
  const parts = item.split('/');
  if (parts.length < 2) return null;

  const projectId = Number(parts[1]);

  return { projectId };
};

// tree items are in the format of `<root>/<project-name>:<project-id>/<resource-type>`
export const parseResourceItem = (item: string | undefined): ProjectInfo | null => {
  const projectInfo = parseProject(item);
  if (!item || !projectInfo) return null;

  const parts = item.split('/');
  if (parts.length < 3) return null;

  const resourceType = parts[2] as NavigationResourceType;
  return { ...projectInfo, resourceType };
};

export const parseAssetId = (item: string | undefined): string | null => {
  if (!item) return null;

  const parts = item.split('/');
  if (parts.length < 6) return null;

  const assetId = parts[parts.length - 1];
  return assetId.length > 0 ? assetId : null;
};

export const WarehouseIconMapping: Record<DataPlatform, Ryecon> = {
  [DataPlatform.Snowflake]: RyeconColorSnowflake,
  [DataPlatform.BigQuery]: RyeconDatabase,
  [DataPlatform.Redshift]: RyeconDatabase,
  [DataPlatform.Databricks]: RyeconDatabase,
};

export const WarehouseAssetTypeIconMapping: Record<WarehouseEntityType, Ryecon> = {
  [WarehouseEntityType.Connection]: RyeconDatabaseConnection,
  [WarehouseEntityType.Database]: RyeconDatabase,
  [WarehouseEntityType.Schema]: RyeconDatabaseSchema,
  [WarehouseEntityType.Table]: RyeconTable,
  [WarehouseEntityType.View]: RyeconTable,
};

export const isWarehouseAssetId = (assetId?: string): boolean => {
  if (!assetId) return false;

  const normalizedAssetId = assetId.toLowerCase();
  const normalizedPlatforms = Object.values(DataPlatform).map((platform) =>
    platform.toLowerCase(),
  );

  return normalizedPlatforms.includes(normalizedAssetId);
};

export const sortDbtProjects = (a: string, b: string, projects: Project[]) => {
  const partsA = a.split('/').filter((part) => !!part);
  const partsB = b.split('/').filter((part) => !!part);
  // sourdough adds a order number in front of each part, so we skip it
  const aId = partsA.pop()?.substring(1);
  const bId = partsB.pop()?.substring(1);
  const aIndex = projects.findIndex((project) => project.id === Number(aId));
  const bIndex = projects.findIndex((project) => project.id === Number(bId));
  if (aIndex === -1) return 1;
  if (bIndex === -1) return -1;
  return aIndex - bIndex;
};

export const sortDbtAssets = (
  a: string,
  b: string,
  projects: Project[],
  validSummaryTypes: string[],
) => {
  const partsA = a.split('/').filter((part) => !!part);
  const partsB = b.split('/').filter((part) => !!part);
  if (partsA.length === partsB.length) {
    if (partsA.length === 2) {
      return sortDbtProjects(a, b, projects);
    }

    // for the resource types, we want to sort them by the order of the validSummaryTypes array
    if (partsA.length === 3) {
      // if they are in the same project, we want to sort by the resource type
      if (partsA[1] === partsB[1]) {
        const aName = partsA.pop()?.substring(1);
        const bName = partsB.pop()?.substring(1);
        if (!aName || !bName) return 0;
        return validSummaryTypes.indexOf(aName) - validSummaryTypes.indexOf(bName);
      }
      // if they are in different projects, we want to sort by the project position
      const aProjectIndex = projects.findIndex(
        (project) => project.id === Number(partsA[1].substring(1)),
      );
      const bProjectIndex = projects.findIndex(
        (project) => project.id === Number(partsB[1].substring(1)),
      );
      return aProjectIndex - bProjectIndex;
    }
  }
  // if they are in different depths, we want to sort by the depth
  return partsA.length - partsB.length;
};

export const scrollToPath = (
  path: string,
  options: {
    parentSelector?: string;
    useAbsolutePosition?: boolean;
  } = {},
) => {
  const { parentSelector, useAbsolutePosition = false } = options;
  const element = document.getElementById(`file-tree-item-${path}`);
  if (!element) return;

  const scrollContainer = element.closest(parentSelector ?? '[class*="overflow-"]');

  if (scrollContainer) {
    // only scroll within the navigation container, don't affect main content
    const elementRect = element.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const isVisible =
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom;
    if (!isVisible) {
      const scrollTop = useAbsolutePosition
        ? scrollContainer.scrollTop + elementRect.top - containerRect.top
        : element.offsetTop - scrollContainer.clientHeight / 2;
      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    }
  } else {
    // Only use native scrollIntoView as absolute last resort when no container is found
    // This can potentially affect sticky headers, so we try to avoid it
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }
};

export const mergeTreeItems = (
  prev: FileTreeItemType[],
  ...items: FileTreeItemType[][]
) => {
  const filteredPrev = prev.filter(
    (item) => !items.flat().some((file) => file.id === item.id),
  );
  return uniqBy([...filteredPrev, ...items.flat()], 'id');
};

export type FavoriteData = {
  type: 'project' | 'asset';
  projectId?: number;
  fileTreeId?: string;
  isFavorite?: boolean;
  assetId?: string;
  name?: string;
  platform?: string;
  assetType?: string;
  environmentId?: string;
};

export type GetMenuActionsData = Omit<FavoriteData, 'isFavorite'> & {
  onActionClick?: (
    action: string,
    data: { projectId?: number; fileTreeId: string },
  ) => void;
};

export const useMenuActions = () => {
  const { accountId } = useDbtCloudAccount();
  const { favoriteProjectIds } = useFavoriteProjectIds(accountId ?? 0);
  const { data: favoriteAssets } = useFavoriteAssets(accountId ?? 0);
  const { mutate: addFavoriteProject } = useAddFavoriteProject();
  const { mutate: deleteFavoriteProject } = useDeleteFavoriteProject();
  const { mutate: addFavoriteAsset } = useAddFavoriteAsset();
  const { mutate: deleteFavoriteAsset } = useDeleteFavoriteAsset();

  const handleFavorite = useCallback(
    ({
      projectId,
      environmentId,
      type,
      isFavorite,
      assetId,
      platform,
      name,
      assetType,
    }: FavoriteData) => {
      if (type === 'project') {
        const operation = isFavorite ? deleteFavoriteProject : addFavoriteProject;
        operation({
          accountId: accountId ?? 0,
          projectId: projectId ?? 0,
        });
      } else if (type === 'asset') {
        if (isFavorite) {
          deleteFavoriteAsset({
            accountId: accountId ?? 0,
            projectId,
            assetId,
            environmentId,
          });
        } else {
          addFavoriteAsset({
            accountId: accountId ?? 0,
            projectId,
            assetId,
            name,
            platform,
            assetType,
            environmentId,
          });
        }
      }
    },
    [
      accountId,
      addFavoriteAsset,
      addFavoriteProject,
      deleteFavoriteAsset,
      deleteFavoriteProject,
    ],
  );

  const getMenuActions = useCallback(
    ({
      fileTreeId,
      projectId,
      type,
      assetId,
      environmentId,
      platform,
      name,
      assetType,
      onActionClick,
    }: GetMenuActionsData): ActionsData['actions'] => {
      const isFavorite =
        type === 'project'
          ? favoriteProjectIds.includes(projectId ?? 0)
          : favoriteAssets?.some(
              (asset) =>
                asset.asset_id === assetId && asset.environment_id === environmentId,
            );
      return [
        {
          items: [
            {
              label: isFavorite ? 'Remove from favorites' : 'Add to favorites',
              value: 'favorite',
              ryecon: isFavorite ? RyeconFavoriteFilled : RyeconFavoriteOutline,
            },
          ],
          onMenuItemClick: (action: string) => {
            const data = {
              projectId,
              fileTreeId: fileTreeId ?? '',
              assetId,
              environmentId,
              platform,
              name,
              assetType,
              isFavorite,
              type,
            };
            if (action === 'favorite') {
              handleFavorite({ ...data });
            }
            onActionClick?.(action, data);
          },
          ryecon: RyeconEllipsisHorizontal,
          menuButtonProps: {
            size: Sizes.xs,
            shouldPortal: true,
            buttonVariant: {
              ryecon: RyeconEllipsisHorizontal,
              variant: 'ryecon',
              tooltip: 'More actions',
            },
          },
        },
      ];
    },
    [favoriteAssets, favoriteProjectIds, handleFavorite],
  );
  return { getMenuActions };
};
